import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Alert, Button } from 'antd';

import { userSignOut } from '@src/appRedux/actions';
import AuthService from '@src/services/AuthService';

import Card from 'containers/App/Auth/components/Card';

import IntlMessages from 'util/IntlMessages';

import InviteOnboardingCard from '../../components/InviteOnboardingCard';
import {
  buildCompleteRegistrationPath,
  rememberCompleteRegistration,
} from '../../utils/completeRegistrationPath';
import {
  forgetInviteToken,
  recallInviteToken,
  rememberInviteToken,
} from '../../utils/inviteTokenStorage';

const INVITE_TOKEN_PARAM = 't';

/** The invite secret travels in the fragment (#t=…) so it never reaches servers or logs. */
export const readInviteToken = ({ hash = '', search = '' }) =>
  new URLSearchParams(hash.replace(/^#/, '')).get(INVITE_TOKEN_PARAM) ||
  new URLSearchParams(search).get(INVITE_TOKEN_PARAM) ||
  undefined;

/** The current URL without the secret, keeping any other query params. */
export const withoutInviteToken = ({ pathname, search = '' }) => {
  const params = new URLSearchParams(search);
  params.delete(INVITE_TOKEN_PARAM);
  const rest = params.toString();
  return { pathname, search: rest ? `?${rest}` : '', hash: '' };
};

const AcceptInvitationV2 = () => {
  const { organizationId: organizationIdParam, userId: userIdParam } = useParams();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const organizationId = organizationIdParam ? decodeURIComponent(organizationIdParam) : '';
  const userId = userIdParam ? decodeURIComponent(userIdParam) : '';
  // Read once: the secret is removed from the address bar right after, and kept for this tab
  // so a reload (or the sign-out redirect below) reopens the same invite.
  const [token] = useState(() => {
    const fromUrl = readInviteToken(location);
    if (fromUrl) {
      rememberInviteToken(organizationId, userId, fromUrl);
      return fromUrl;
    }
    return recallInviteToken(organizationId, userId);
  });
  const { authUser } = useSelector(({ auth: userData }) => userData);
  const otherAccountSignedIn = Boolean(authUser) && authUser !== userId;

  useEffect(() => {
    // Keep the secret out of browser history, later navigations and monitoring URLs.
    if (readInviteToken(location)) {
      history.replace(withoutInviteToken(location));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (otherAccountSignedIn) {
    // Accepting signs in as the invitee, so the other session ends first, explicitly.
    return (
      <Card>
        <Alert
          type="info"
          message=""
          description={
            <>
              <IntlMessages id="users.invitation.otherAccount.message" />
              <div className="gx-mt-2">
                <Button
                  type="primary"
                  onClick={() => dispatch(userSignOut({ redirectTo: location.pathname }))}
                >
                  <IntlMessages id="users.invitation.otherAccount.signOut" />
                </Button>
              </div>
            </>
          }
        />
      </Card>
    );
  }

  const loadContext = async () => {
    if (!organizationId || !userId) {
      return null;
    }
    return new AuthService().getInviteAcceptanceContext(organizationId, userId, token);
  };

  const submit = async ({ password, organizationId: orgId, userId: uid, user }) => {
    await new AuthService().updateAndSignInV2({
      password,
      organizationId: orgId,
      userId: uid,
      token,
      user,
    });
    forgetInviteToken(organizationId, userId);
  };

  // Existing accounts accept by signing in and finishing on /complete-registration.
  const onSignInRequired = () => {
    if (authUser === userId) {
      history.push(buildCompleteRegistrationPath(organizationId));
      return;
    }
    rememberCompleteRegistration(organizationId);
    history.push('/signin');
  };

  return (
    <InviteOnboardingCard
      loadContext={loadContext}
      submit={submit}
      onSignInRequired={onSignInRequired}
      fallback={{ organizationId, userId }}
    />
  );
};

export default AcceptInvitationV2;
